// A room's glazings (docs/claude/glazings.md): each outer door with fixed glass
// beside it and no wall between — the door and the panes touching it along its
// wall, and, round a 90° corner, the panes the run turns onto — worked out as
// the PT10 spec says (glazing.ts); and a steam room's outer door with no glass
// beside it, the PT9 single door (singleDoor.ts). Each placed in the room: what the 3D view
// meshes, the plan cuts, the sections slice and the card shows. Pure and
// DOM-free (test/glazing.test.mjs).

import type {
  GlassSegment,
  ProductInstance,
  SaunaDoor,
  Vec2,
} from '../shared/types';
import { wallThicknessOf } from './dent';
import { instanceToWorld } from './geometry';
import {
  type GlazingInput,
  type GlazingMaterial,
  glazingOptions,
  type GlazingPlan,
  type GlazingSolid,
  glazingSolids,
  type GlazingStuff,
  holeIn,
  type P3,
  planGlazing,
  solidPieces,
  Z_OUT,
} from './glazing';
import { doorKindRule } from './door';
import type { DoorSwing } from './doorSwing';
import { doorTop } from './heights';
import {
  planSingleDoor,
  SINGLE_GAP,
  SINGLE_Z_OUT,
  singleDoorSolids,
} from './singleDoor';
import {
  isPolyRoom,
  outerDoorSpans,
  polyEdges,
  wallView,
} from './polyRoom';

/** A glazing's material by its room: aluminium in a steam room, wood in a sauna;
 *  any other room has none. */
export function glazingMaterialOf(room: Pick<ProductInstance, 'category'>): GlazingMaterial | null {
  return room.category === 'steamroom' ? 'aluminium' : room.category === 'sauna' ? 'wood' : null;
}

/** Where the room's outer wall face lies in a glazing's frame (z from the glass
 *  mid-plane), with no cladding. An aluminium jamb laps the masonry from
 *  outside, in configuration 1 as in 4 (the same profile, in line): the face is
 *  0.3 mm behind the jamb's back (10.73), so the two never share a plane — taken
 *  off the profile's outer face (15.73) it sat 5 mm inside the wall. A wood frame
 *  is flush with the wall's face (34.5), in configuration 1 as in 4: at 22.5 the
 *  exposed frame stood 12 mm proud of it. */
export function wallFaceZ(mat: GlazingMaterial, _config: 1 | 4): number {
  return mat === 'wood' ? Z_OUT.wood : 10.43;
}

/** One glazing in a room. Stations are along the wall (corner to corner) on its
 *  outer face; `x` is the glazing's frame x as seen from outside. */
export interface RoomGlazing {
  /** PT10, a door with glass beside it; PT9, a single door (no panes). */
  system: 'pt10' | 'pt9';
  /** The door it is built round (its id names the glazing). */
  door: SaunaDoor;
  /** Its wall (in the room's wall view) and the run on it, wall to wall. */
  wall: number;
  run: [number, number];
  /** The panes in it, by id: along the front wall, and round the corner. */
  panes: string[];
  sidePanes: string[];
  /** The front panes left and right of the door, seen from outside. */
  panesLeft: string[];
  panesRight: string[];
  input: GlazingInput;
  plan: GlazingPlan;
  /** Glazing frame → the room's local plan: x = 0, z = the wall face at `origin`. */
  origin: Vec2;
  xAxis: Vec2;
  out: Vec2;
  zFace: number;
}

const RUN_TOL = 1;
const RIGHT_TOL = Math.sin((0.5 * Math.PI) / 180);

const memo = new Map<string, RoomGlazing[]>();

/** The room's glazings (none for a room of another kind or a legacy rectangle). */
export function roomGlazings(room: ProductInstance): RoomGlazing[] {
  const mat = glazingMaterialOf(room);
  if (!mat || !isPolyRoom(room) || !(room.doors?.length || room.door) || (mat !== 'aluminium' && !room.glass?.length)) return [];
  const key = JSON.stringify([room.category, room.polygon, room.polygonJoints, room.polygonRadii, room.glass, room.doors, room.door, room.glassHeight, room.wallThickness]);
  const hit = memo.get(key);
  if (hit) return hit;
  const out = findGlazings(room, mat);
  if (memo.size > 200) memo.clear();
  memo.set(key, out);
  return out;
}

/** The glazing a door is the door of, or null. */
export function glazingOfDoor(room: ProductInstance, doorId: string | undefined | null): RoomGlazing | null {
  if (!doorId) return null;
  return roomGlazings(room).find((g) => g.door.id === doorId) ?? null;
}

/** The glazing a pane belongs to, or null. */
export function glazingOfPane(room: ProductInstance, paneId: string | undefined | null): RoomGlazing | null {
  if (!paneId) return null;
  return roomGlazings(room).find((g) => g.panes.includes(paneId) || g.sidePanes.includes(paneId)) ?? null;
}

/** Whether a pane or door segment is built by a glazing (so not as plain glass). */
export function inGlazing(room: ProductInstance, seg: { id?: string } | null | undefined, doorId?: string): boolean {
  if (doorId) return !!glazingOfDoor(room, doorId);
  return !!seg?.id && !!glazingOfPane(room, seg.id);
}

function findGlazings(room: ProductInstance, mat: GlazingMaterial): RoomGlazing[] {
  const view = wallView(room);
  const edges = polyEdges(view);
  const n = edges.length;
  const realDoors = room.doors ?? [];
  const claimed = new Set<string>();
  const doorSpans = outerDoorSpans(view);
  const panesOn = (w: number): GlassSegment[] => (view.glass ?? [])
    .filter((g) => g.edge === w && g.innerWallId == null && g.id)
    .sort((a, b) => Math.min(a.start, a.end) - Math.min(b.start, b.end));
  const lo = (g: GlassSegment): number => Math.min(g.start, g.end), hi = (g: GlassSegment): number => Math.max(g.start, g.end);
  const touchesDoor = (w: number, a: number, b: number): boolean => doorSpans.some((d) => d.edge === w && d.end >= a - RUN_TOL && d.start <= b + RUN_TOL);
  const out: RoomGlazing[] = [];
  for (const sp of doorSpans) {
    const door = realDoors.find((d) => d.id === sp.door.id);
    if (!door?.id) continue;
    const w = sp.edge, e = edges[w];
    if (!e) continue;
    const glass = panesOn(w).filter((g) => !claimed.has(g.id!));
    let a = sp.start, b = sp.end;
    const left: GlassSegment[] = [], right: GlassSegment[] = [];
    for (let g = glass.find((q) => Math.abs(hi(q) - a) <= RUN_TOL); g; g = glass.find((q) => Math.abs(hi(q) - a) <= RUN_TOL && !left.includes(q))) { left.push(g); a = lo(g); }
    for (let g = glass.find((q) => Math.abs(lo(q) - b) <= RUN_TOL); g; g = glass.find((q) => Math.abs(lo(q) - b) <= RUN_TOL && !right.includes(q))) { right.push(g); b = hi(g); }
    const kind = doorKindRule(room.category, false, left.length + right.length > 0);
    if (kind === 'plain') continue;
    // Seen from outside the wall runs to the right along its direction when the
    // outward normal turned clockwise is it.
    const outN = { x: -e.normal.x, y: -e.normal.y };
    const rightSeen = { x: -outN.y, y: outN.x };
    const sgn = e.dir.x * rightSeen.x + e.dir.y * rightSeen.y >= 0 ? 1 : -1;
    const xAxis = { x: e.dir.x * sgn, y: e.dir.y * sgn };
    const startIsLeft = sgn > 0;
    if (kind === 'pt9') {
      out.push(singleDoorOf(door, sp, w, e, xAxis, outN, startIsLeft, doorTop(room, door)));
      continue;
    }
    const panesL = startIsLeft ? left : right, panesR = startIsLeft ? right : left;
    const layout = panesL.length && panesR.length ? 'centro' : panesR.length ? 'sinistra' : 'destra';
    const hingeAtStart = door.hinge === 'left';
    const hinge = hingeAtStart === startIsLeft ? 'sx' : 'dx';
    const opts = glazingOptions(door.glazing);
    const zFace = wallFaceZ(mat, opts.config), zo = Z_OUT[mat];
    // A corner: the run reaching a 90° corner whose next wall is glazed from it.
    let corner: { side: 'L' | 'R'; D: number } | null = null;
    let sidePanes: GlassSegment[] = [];
    const tryCorner = (atEnd: boolean): void => {
      if (corner) return;
      if (atEnd ? b < e.len - RUN_TOL : a > RUN_TOL) return;
      const v = atEnd ? (w + 1) % n : w;
      if (room.polygonRadii?.[viewVertexOf(room, v)]) return;
      const w2 = atEnd ? (w + 1) % n : (w - 1 + n) % n, e2 = edges[w2];
      if (!e2 || w2 === w || Math.abs(e.dir.x * e2.dir.x + e.dir.y * e2.dir.y) > RIGHT_TOL) return;
      const g2 = panesOn(w2).filter((g) => !claimed.has(g.id!));
      const run: GlassSegment[] = [];
      if (atEnd) { let c = 0; for (let g = g2.find((q) => lo(q) <= RUN_TOL); g; g = g2.find((q) => Math.abs(lo(q) - c) <= RUN_TOL && !run.includes(q))) { run.push(g); c = hi(g); } }
      else { let c = e2.len; for (let g = g2.find((q) => hi(q) >= e2.len - RUN_TOL); g; g = g2.find((q) => Math.abs(hi(q) - c) <= RUN_TOL && !run.includes(q))) { run.push(g); c = lo(g); } }
      if (!run.length) return;
      const s0 = Math.min(...run.map(lo)), s1 = Math.max(...run.map(hi));
      if (touchesDoor(w2, s0, s1)) return;
      const endIsRight = atEnd === startIsLeft;
      corner = { side: endIsRight ? 'R' : 'L', D: s1 - s0 + (zo - zFace) };
      sidePanes = run;
    };
    tryCorner(true);
    tryCorner(false);
    const build = (c: { side: 'L' | 'R'; D: number } | null): { input: GlazingInput; plan: GlazingPlan; origin: Vec2 } => {
      const W = b - a + (c ? zo - zFace : 0);
      const leftExtra = c?.side === 'L' ? zo - zFace : holeIn(mat);
      const frameW = W + (c ? 1 : 2) * holeIn(mat);
      const sLeft = startIsLeft ? a : b;
      const xOf = (s: number): number => sgn * (s - sLeft) + leftExtra;
      const off = layout === 'centro' ? xOf((sp.start + sp.end) / 2) - frameW / 2 : 0;
      const input: GlazingInput = { ...opts, mat, W, H: doorTop(room, door), Dw: door.width, layout, hinge, off, corner: c };
      const origin = { x: e.a.x + e.dir.x * sLeft - xAxis.x * leftExtra, y: e.a.y + e.dir.y * sLeft - xAxis.y * leftExtra };
      return { input, plan: planGlazing(input), origin };
    };
    let { input, plan, origin } = build(corner as { side: 'L' | 'R'; D: number } | null);
    // A corner the spec refuses (hinge side against it) leaves the return as
    // glass and the run without it; its warning stays.
    if (input.corner && !plan.corner) {
      const refused = plan.warnings.filter((w) => w.kind.startsWith('corner'));
      ({ input, plan, origin } = build(null));
      plan = { ...plan, warnings: [...refused, ...plan.warnings] };
    }
    const all = [...left, ...right];
    all.forEach((g) => claimed.add(g.id!));
    if (plan.corner) sidePanes.forEach((g) => claimed.add(g.id!));
    out.push({ system: 'pt10', door, wall: w, run: [a, b], panes: all.map((g) => g.id!), panesLeft: panesL.map((g) => g.id!), panesRight: panesR.map((g) => g.id!), sidePanes: plan.corner ? sidePanes.map((g) => g.id!) : [], input, plan, origin, xAxis, out: outN, zFace });
  }
  return out;
}

/** A single door (PT9): its frame 5 mm in from each side of the opening, its
 *  outer face flush with the wall's outer face. */
function singleDoorOf(door: SaunaDoor, sp: { start: number; end: number }, w: number, e: { a: Vec2; dir: Vec2 }, xAxis: Vec2, outN: Vec2, startIsLeft: boolean, H: number): RoomGlazing {
  const hingeAtStart = door.hinge === 'left';
  const input: GlazingInput = { ...glazingOptions(door.glazing), config: 1, threshold: false, mat: 'aluminium', W: sp.end - sp.start, H, Dw: sp.end - sp.start - 2 * SINGLE_GAP - 54, layout: 'destra', hinge: hingeAtStart === startIsLeft ? 'sx' : 'dx', off: 0, corner: null };
  const sLeft = startIsLeft ? sp.start : sp.end;
  const origin = { x: e.a.x + e.dir.x * sLeft + xAxis.x * SINGLE_GAP, y: e.a.y + e.dir.y * sLeft + xAxis.y * SINGLE_GAP };
  return { system: 'pt9', door, wall: w, run: [sp.start, sp.end], panes: [], sidePanes: [], panesLeft: [], panesRight: [], input, plan: planSingleDoor(input), origin, xAxis, out: outN, zFace: SINGLE_Z_OUT };
}

/** A glazing's solids in its own frame, its door turned `open` degrees. */
export function solidsOf(g: Pick<RoomGlazing, 'system' | 'plan'>, open = 0): GlazingSolid[] {
  return g.system === 'pt9' ? singleDoorSolids(g.plan, open) : glazingSolids(g.plan, open);
}

/** The room's own vertex index of wall `w`'s first corner. */
function viewVertexOf(room: ProductInstance, w: number): number {
  if (!room.polygonJoints) return w;
  let k = -1;
  for (let v = 0; v < (room.polygon?.length ?? 0); v++) if (!room.polygonJoints[v] && ++k === w) return v;
  return w;
}

/** What a click on a glazing's solid takes: the door's (its leaf and what it
 *  carries), else the pane on its side of the door — round the corner, the
 *  first pane there. */
export function glazingPartOf(g: RoomGlazing, s: GlazingSolid): { kind: 'door' | 'pane'; id: string } {
  if (s.part.kind === 'door' || !(g.panesLeft.length + g.panesRight.length)) return { kind: 'door', id: g.door.id! };
  if (s.part.kind === 'side' && g.sidePanes.length) return { kind: 'pane', id: g.sidePanes[0] };
  const v = g.plan.v, c = g.plan.mirror ? v.W - (v.door[0] + v.door[1]) / 2 : (v.door[0] + v.door[1]) / 2;
  let x0 = Infinity, x1 = -Infinity;
  for (const l of s.ends[0]) for (const q of l) { x0 = Math.min(x0, q.x); x1 = Math.max(x1, q.x); }
  const left = (x0 + x1) / 2 < c;
  const ids = left ? (g.panesLeft.length ? g.panesLeft : g.panesRight) : (g.panesRight.length ? g.panesRight : g.panesLeft);
  return { kind: 'pane', id: ids[0] };
}

/** The glazing a feature (a door or a pane, by id) is part of, in the room. */
export function glazingOfFeature(room: ProductInstance, f: { kind: string; id?: string } | null | undefined): RoomGlazing | null {
  if (!f?.id) return null;
  return f.kind === 'door' ? glazingOfDoor(room, f.id) : f.kind === 'pane' ? glazingOfPane(room, f.id) : null;
}

/** A point of the glazing's frame in the room's local plan (x, y) and height (z). */
export function glazingToLocal(g: RoomGlazing, q: P3): P3 {
  const dz = q.z - g.zFace;
  return { x: g.origin.x + g.xAxis.x * q.x + g.out.x * dz, y: g.origin.y + g.xAxis.y * q.x + g.out.y * dz, z: q.y };
}

/** A glazing's solid in the room's local frame (z up). */
export interface LocalSolid { stuff: GlazingStuff; part: GlazingSolid['part']; solid: GlazingSolid; ends: [P3[][], P3[][]] }

/** The door turned open, as the plan draws a door (beside it closed, as the
 *  PT9 drawing does) and the 3D view shows it; sections draw it closed, as the
 *  drawing's front view. */
export const GLAZING_OPEN = 90;

const SOLIDS = new Map<string, LocalSolid[]>();

/** A glazing's solids in the room's local frame. */
export function glazingLocalSolids(g: RoomGlazing, open = GLAZING_OPEN): LocalSolid[] {
  const key = JSON.stringify([g.system, g.input, g.origin, g.xAxis, g.zFace, open]);
  const hit = SOLIDS.get(key);
  if (hit) return hit;
  const res = solidsOf(g, open).map((s) => ({ stuff: s.stuff, part: s.part, solid: s, ends: [s.ends[0].map((l) => l.map((q) => glazingToLocal(g, q))), s.ends[1].map((l) => l.map((q) => glazingToLocal(g, q)))] as [P3[][], P3[][]] }));
  if (SOLIDS.size > 100) SOLIDS.clear();
  SOLIDS.set(key, res);
  return res;
}

// Every result below is kept with the glazing (or the solid) it was made from:
// roomGlazings and glazingLocalSolids hand back the same objects until the room
// is edited, so a frame that only zooms or pans recomputes nothing. Shared:
// callers never mutate what they get.
const PIECES = new WeakMap<LocalSolid, P3[][]>();

/** A solid's convex pieces in the room's local frame. */
export function localPieces(g: RoomGlazing, s: LocalSolid): P3[][] {
  let out = PIECES.get(s);
  if (!out) PIECES.set(s, out = solidPieces(s.solid).map((pc) => pc.map((q) => glazingToLocal(g, q))));
  return out;
}

type WorldSolid = { pieces: P3[][]; stuff: GlazingStuff; part: { kind: 'door' | 'pane'; id: string }; color: string; seeThrough: boolean };
const WORLD = new WeakMap<RoomGlazing, Map<string, WorldSolid[]>>();

/** A glazing's solids as convex pieces in the world (x, y plan, z above the
 *  room's floor), the door closed, each with what a click takes and its look —
 *  what a section slices. `wallHex`: the room's walls' colour in this view, a wood frame's. */
export function glazingWorldSolids(room: ProductInstance, g: RoomGlazing, wallHex?: string): WorldSolid[] {
  const key = `${room.x},${room.y},${room.rotation},${wallHex ?? ''}`;
  let byPlace = WORLD.get(g);
  if (!byPlace) WORLD.set(g, byPlace = new Map());
  const hit = byPlace.get(key);
  if (hit) return hit;
  if (byPlace.size > 8) byPlace.clear();
  const out = glazingLocalSolids(g, 0).map((s) => {
    const look = glazingLook(g, s.stuff, wallHex);
    return {
      pieces: localPieces(g, s).map((pc) => pc.map((q) => ({ ...instanceToWorld(room, q), z: q.z }))),
      stuff: s.stuff,
      part: glazingPartOf(g, s.solid),
      color: look.color,
      seeThrough: look.opacity < 1,
    };
  });
  byPlace.set(key, out);
  return out;
}

// ---- Colours -------------------------------------------------------------------

const FINISH_HEX: Record<string, { profile: string; hardware: string }> = {
  '02': { profile: '#a9adb2', hardware: '#b2b6bb' },
  '05': { profile: '#2c2d30', hardware: '#303134' },
  '17': { profile: '#baa886', hardware: '#c0ae8c' },
};
const GLASS_HEX: Record<string, string> = { clear: '#d2e6e2', low: '#f1f6f5', acid: '#e7eae8', bronze: '#8c6b4b', grey: '#5c6165', refl: '#b9c3ca' };

/** A wood frame's colour when the view gives no walls' colour. */
export const GLAZING_WOOD = '#d8b98a';

/** What a solid looks like: its colour, and how see-through (1 = opaque). A
 *  wood frame is the sauna's walls' wood: `wallHex`, the walls' colour in the
 *  view asking. */
export function glazingLook(g: RoomGlazing, stuff: GlazingStuff, wallHex?: string): { color: string; opacity: number; metal: boolean } {
  const f = FINISH_HEX[g.input.finish] ?? FINISH_HEX['02'];
  switch (stuff) {
    case 'profile': return { color: f.profile, opacity: 1, metal: true };
    case 'hardware': return { color: f.hardware, opacity: 1, metal: true };
    case 'wood': return { color: wallHex || GLAZING_WOOD, opacity: 1, metal: false };
    case 'glass': return { color: GLASS_HEX[g.input.glass] ?? GLASS_HEX.clear, opacity: g.input.glass === 'acid' ? 0.75 : g.input.glass === 'grey' || g.input.glass === 'bronze' ? 0.55 : 0.3, metal: false };
    case 'seal': return { color: '#f1f5f4', opacity: 0.62, metal: false };
    case 'silicone': return { color: '#eef3f2', opacity: 0.35, metal: false };
    case 'magnet': return { color: '#4a4d50', opacity: 1, metal: false };
    case 'gasket': return { color: g.input.finish === '02' ? '#8e9295' : '#1d1e20', opacity: 1, metal: false };
  }
}

// ---- The plan: a cut through the glazing ------------------------------------------

/** The height the plan cuts a glazing at (mm above the floor). */
export const GLAZING_PLAN_CUT = 1000;

export type GlazingCut = Array<{ stuff: GlazingStuff; solid: GlazingSolid; rings: Vec2[][]; exact: boolean }>;
const CUTS = new WeakMap<RoomGlazing, GlazingCut>();

/** What the plan draws of a glazing, as the PT9 drawing does: the door open
 *  AND closed, each solid's slice at the cut height in the room's local plan,
 *  with its stuff — glass first, so the frame shows over it where they meet —,
 *  but the hardware (hinges, handles) seen from above whatever its height. An
 *  upright solid (one whose every edge along it crosses the cut, or hardware
 *  standing straight up) is drawn exactly, its section's outline and holes
 *  (`exact`); any other as its convex pieces' slices (seen from above, their
 *  outlines), which overlap only along shared sides. */
export function glazingPlanCut(g: RoomGlazing, h = GLAZING_PLAN_CUT): GlazingCut {
  if (h !== GLAZING_PLAN_CUT) return planCut(g, h);
  let out = CUTS.get(g);
  if (!out) CUTS.set(g, out = planCut(g, h));
  return out;
}

function planCut(g: RoomGlazing, h: number): GlazingCut {
  const out: GlazingCut = [];
  const closed = glazingLocalSolids(g, 0).filter((s) => s.part.kind === 'door');
  for (const s of [...glazingLocalSolids(g), ...closed]) {
    const [e0, e1] = s.ends;
    if (s.stuff === 'hardware') {
      const upright = e0.every((l, k) => l.every((p, j) => Math.abs(e1[k][j].x - p.x) < 1e-6 && Math.abs(e1[k][j].y - p.y) < 1e-6));
      if (upright) out.push({ stuff: s.stuff, solid: s.solid, rings: e0.map((l) => l.map((p) => ({ x: p.x, y: p.y }))), exact: true });
      else {
        const rings = localPieces(g, s).map((pc) => hull2(pc.map((p) => ({ x: p.x, y: p.y })))).filter((r) => r.length >= 3);
        if (rings.length) out.push({ stuff: s.stuff, solid: s.solid, rings, exact: false });
      }
      continue;
    }
    // Most of a glazing (its head, the seals and hinges up there) never reaches
    // the cut: skipped before it is broken into pieces.
    let lo = Infinity, hi = -Infinity;
    for (const e of s.ends) for (const l of e) for (const q of l) { if (q.z < lo) lo = q.z; if (q.z > hi) hi = q.z; }
    if (lo > h || hi < h) continue;
    const crosses = e0.every((l, k) => l.every((p, j) => (p.z - h) * (e1[k][j].z - h) <= 0 && Math.abs(e1[k][j].z - p.z) > 1e-9));
    if (crosses) {
      const rings = e0.map((l, k) => l.map((p, j) => {
        const q = e1[k][j], t = (h - p.z) / (q.z - p.z);
        return { x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t };
      }));
      out.push({ stuff: s.stuff, solid: s.solid, rings, exact: true });
      continue;
    }
    const rings: Vec2[][] = [];
    for (const pc of localPieces(g, s)) {
      const r = sliceAt(pc, h);
      if (r) rings.push(r);
    }
    if (rings.length) out.push({ stuff: s.stuff, solid: s.solid, rings, exact: false });
  }
  return out.sort((p, q) => (p.stuff === 'glass' ? 0 : 1) - (q.stuff === 'glass' ? 0 : 1));
}

/** A convex piece's slice at height h (its hull), or null. */
export function sliceAt(pc: readonly P3[], h: number): Vec2[] | null {
  const pts: Vec2[] = [];
  for (let i = 0; i < pc.length; i++) {
    const a = pc[i];
    if (Math.abs(a.z - h) < 1e-9) pts.push({ x: a.x, y: a.y });
    for (let j = i + 1; j < pc.length; j++) {
      const b = pc[j];
      if ((a.z - h) * (b.z - h) < 0) {
        const t = (h - a.z) / (b.z - a.z);
        pts.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
      }
    }
  }
  const hl = hull2(pts);
  return hl.length >= 3 ? hl : null;
}

export function hull2(pts: Vec2[]): Vec2[] {
  const p = pts.slice().sort((a, b) => a.x - b.x || a.y - b.y);
  if (p.length < 3) return p;
  const cross = (o: Vec2, a: Vec2, b: Vec2): number => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Vec2[] = [], upper: Vec2[] = [];
  for (const q of p) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], q) <= 1e-9) lower.pop(); lower.push(q); }
  for (let k = p.length - 1; k >= 0; k--) { const q = p[k]; while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], q) <= 1e-9) upper.pop(); upper.push(q); }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

const SWINGS = new WeakMap<RoomGlazing, DoorSwing | null>();

/** The door's swing in the plan: the hinge axis, the open leaf's tip and the
 *  closed leaf's free edge (room local), for the arc. */
export function glazingSwing(g: RoomGlazing): DoorSwing | null {
  let out = SWINGS.get(g);
  if (out === undefined) SWINGS.set(g, out = swingOf(g));
  return out;
}

function swingOf(g: RoomGlazing): DoorSwing | null {
  const closed = solidsOf(g, 0);
  const leaf = closed.find((s) => s.stuff === 'glass' && s.part.kind === 'door');
  const knuckle = closed.find((s) => s.hinge && s.part.kind === 'door');
  if (!leaf || !knuckle) return null;
  const xs = leaf.ends[0].flat().map((q) => q.x);
  const pts = knuckle.ends[0][0];
  const ax = pts.reduce((t, q) => t + q.x, 0) / pts.length, az = pts.reduce((t, q) => t + q.z, 0) / pts.length;
  const x0 = Math.min(...xs), x1 = Math.max(...xs), free = Math.abs(ax - x0) > Math.abs(ax - x1) ? x0 : x1, r = Math.abs(free - ax);
  const p = (x: number, z: number): Vec2 => { const l = glazingToLocal(g, { x, y: 0, z }); return { x: l.x, y: l.y }; };
  return { hinge: p(ax, az), open: p(ax, az + r), closed: p(free, 0), radius: r };
}

/** The wall thickness a glazing sits in (for the 3D wall over it). */
export const glazingWall = (room: ProductInstance): number => wallThicknessOf(room);
